import React from 'react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { HeartIcon, TargetIcon, UsersIcon, ShieldIcon } from 'lucide-react';
export default function About() {
  const values = [{
    icon: HeartIcon,
    title: 'Care-Focused',
    description: 'We put the needs of families and carers at the heart of everything we build.'
  }, {
    icon: TargetIcon,
    title: 'Simple Solutions',
    description: 'Technology should be easy to use, not complicated. We design with clarity in mind.'
  }, {
    icon: UsersIcon,
    title: 'Community Support',
    description: 'We are here to help families support their loved ones with confidence.'
  }, {
    icon: ShieldIcon,
    title: 'Trust & Security',
    description: 'Your data is protected with industry-leading security and GDPR compliance.'
  }];
  return <div className="w-full" data-id="element-125">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-light-grey py-16" data-id="element-126">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-127">
          <h1 className="text-4xl md:text-5xl font-bold text-navy font-heading mb-6" data-id="element-128">
            About Artisanfix
          </h1>
          <p className="text-xl text-dark leading-relaxed" data-id="element-129">
            Artisanfix is a UK-based team focused on making technology easy for
            everyone. We create simple digital tools that help carers and
            families support older relatives with confidence.
          </p>
        </div>
      </section>
      {/* Mission Statement */}
      <section className="py-16 bg-white" data-id="element-130">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-131">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-132">
            <div data-id="element-133">
              <h2 className="text-3xl font-bold text-navy font-heading mb-6" data-id="element-134">
                Our Mission
              </h2>
              <p className="text-lg text-dark mb-4" data-id="element-135">
                To reduce worry, save time, and help people live independently
                for longer.
              </p>
              <p className="text-dark mb-6" data-id="element-136">
                We believe that caring for loved ones should not be stressful or
                overwhelming. Our tools are designed to give families peace of
                mind and help older adults maintain their independence with
                dignity.
              </p>
              <Button href="/contact" data-id="element-137">Get in Touch</Button>
            </div>
            <div className="flex justify-center" data-id="element-138">
              <img src="https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Team working together on digital health solutions" className="rounded-lg shadow-lg max-w-full h-auto" data-id="element-139" />
            </div>
          </div>
        </div>
      </section>
      {/* Values */}
      <section className="py-16 bg-light-grey" data-id="element-140">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-141">
          <div className="text-center mb-16" data-id="element-142">
            <h2 className="text-3xl font-bold text-navy font-heading mb-4" data-id="element-143">
              What We Stand For
            </h2>
            <p className="text-lg text-dark max-w-3xl mx-auto" data-id="element-144">
              Our values guide everything we do at Artisanfix.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-145">
            {values.map((value, index) => <Card key={index} data-id="element-146">
                <div className="flex items-start" data-id="element-147">
                  <div className="flex-shrink-0" data-id="element-148">
                    <div className="rounded-full bg-light-grey p-3 w-12 h-12 flex items-center justify-center" data-id="element-149">
                      <value.icon className="h-6 w-6 text-navy" data-id="element-150" />
                    </div>
                  </div>
                  <div className="ml-4" data-id="element-151">
                    <h3 className="text-xl font-bold text-navy mb-2" data-id="element-152">
                      {value.title}
                    </h3>
                    <p className="text-dark" data-id="element-153">{value.description}</p>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </section>
      {/* Team Section */}
      <section className="py-16 bg-white" data-id="element-154">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-155">
          <h2 className="text-3xl font-bold text-navy font-heading mb-6" data-id="element-156">
            Built by People Who Care
          </h2>
          <p className="text-lg text-dark mb-8" data-id="element-157">
            Our team has personal experience with the challenges of caring for
            elderly relatives. We have built Artisanfix to solve real problems
            that families face every day.
          </p>
          <div className="flex justify-center mb-8" data-id="element-158">
            <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Diverse team collaborating on health technology solutions" className="rounded-lg shadow-lg max-w-full h-auto" data-id="element-159" />
          </div>
          <p className="text-dark mb-8" data-id="element-160">
            We are based in the UK and committed to providing excellent support
            to all our users. If you have questions or need help, we are here
            for you.
          </p>
          <Button href="/contact" size="lg" data-id="element-161">
            Contact Our Team
          </Button>
        </div>
      </section>
      {/* CTA Section */}
      <section className="bg-navy text-white py-16" data-id="element-162">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-163">
          <h2 className="text-3xl font-bold font-heading mb-4" data-id="element-164">
            Join Our Community
          </h2>
          <p className="text-lg text-white/80 max-w-3xl mx-auto mb-8" data-id="element-165">
            Discover how Artisanfix can help your family stay organised and
            reduce stress.
          </p>
          <Button variant="secondary" size="lg" href="/contact" data-id="element-166">
            Get Started
          </Button>
        </div>
      </section>
    </div>;
}